/**
 * Campos que se pueden cambiar en MASA desde los listados.
 *
 * Es el espejo exacto de `BULK_EDIT_FIELDS` del backend
 * (`backend/app/blueprints/bulk.py`): si aquí no está, el backend lo rechaza
 * con un 400 — y al revés, un campo que el backend no conozca no se pinta.
 *
 * REGLA: aquí NO van identificadores ni campos únicos (nombre, código,
 * número de serie, MAC, IP, clave de licencia…). Un cambio masivo los
 * duplicaría; eso se cambia ficha a ficha. Tampoco la ubicación/rack: para eso
 * está el botón «📍 Ubicación / rack» (`BulkPlacementAction`), que valida
 * huecos y sedes.
 *
 * Tipos:
 *   'text'   → caja de texto
 *   'select' → desplegable con `options` [{ value, label }]
 *   'bool'   → interruptor (Sí / No)
 *   'date'   → `<input type="date">`
 */

const opts = (pairs) => pairs.map(([value, label]) => ({ value, label }));

const STATUS_INFRA = opts([
  ['active', 'Activo'],
  ['maintenance', 'Mantenimiento'],
  ['decom', 'Decom'],
  ['retired', 'Retirado'],
]);

const STATUS_ASSET = opts([
  ['active', 'Activo'],
  ['inactive', 'Inactivo'],
  ['maintenance', 'Mantenimiento'],
]);

const STATUS_ASSET_LONG = opts([
  ['active', 'Activo'],
  ['inactive', 'Inactivo'],
  ['maintenance', 'Mantenimiento'],
  ['decommissioned', 'Dado de baja'],
]);

const STATUS_HA = opts([
  ['active', 'Activo'],
  ['standby', 'Standby'],
  ['maintenance', 'Mantenimiento'],
  ['decom', 'Decom'],
]);

const ENVIRONMENT = opts([
  ['', 'Sin entorno'],
  ['prod', 'Producción'],
  ['pre', 'Pre-producción'],
  ['dev', 'Desarrollo'],
]);

export const BULK_FIELD_REGISTRY = {
  servers: [
    { key: 'status', label: 'Estado', type: 'select', options: STATUS_INFRA },
    { key: 'environment', label: 'Entorno', type: 'select', options: ENVIRONMENT },
    { key: 'owner', label: 'Propietario', type: 'text' },
    { key: 'manufacturer', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
    { key: 'os', label: 'Sistema operativo', type: 'text' },
    { key: 'hypervisor', label: 'Hipervisor', type: 'text' },
    { key: 'cluster', label: 'Clúster', type: 'text' },
    {
      key: 'license_status',
      label: 'Estado de licencia',
      type: 'select',
      options: opts([['vigente', 'Vigente'], ['expirada', 'Expirada'], ['no_aplica', 'No aplica']]),
    },
  ],

  storage_arrays: [
    { key: 'status', label: 'Estado', type: 'select', options: STATUS_INFRA },
    { key: 'environment', label: 'Entorno', type: 'select', options: ENVIRONMENT },
    { key: 'owner', label: 'Propietario', type: 'text' },
    { key: 'manufacturer', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
    { key: 'os_distribution', label: 'Distribución / SO', type: 'text' },
  ],

  net_devices: [
    { key: 'status', label: 'Estado', type: 'select', options: STATUS_INFRA },
    { key: 'manufacturer', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
  ],

  workstations: [
    { key: 'status', label: 'Estado', type: 'select', options: STATUS_ASSET_LONG },
    {
      key: 'device_type',
      label: 'Tipo de equipo',
      type: 'select',
      options: opts([['pc', 'Sobremesa'], ['laptop', 'Portátil']]),
    },
    { key: 'manufacturer', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
    { key: 'os_name', label: 'Sistema operativo', type: 'text' },
    { key: 'assigned_to', label: 'Asignado a', type: 'text' },
  ],

  printers: [
    { key: 'status', label: 'Estado', type: 'select', options: STATUS_ASSET },
    { key: 'manufacturer', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
    { key: 'has_ethernet', label: 'Ethernet', type: 'bool' },
    { key: 'has_wifi', label: 'WiFi', type: 'bool' },
  ],

  peripherals: [
    { key: 'status', label: 'Estado', type: 'select', options: STATUS_ASSET_LONG },
    { key: 'manufacturer', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
  ],

  ip_phones: [
    {
      key: 'status',
      label: 'Estado',
      type: 'select',
      options: opts([['active', 'Activo'], ['inactive', 'Inactivo']]),
    },
    { key: 'model', label: 'Modelo', type: 'text' },
  ],

  wifi_aps: [
    {
      key: 'status',
      label: 'Estado',
      type: 'select',
      options: opts([
        ['active', 'Activo'],
        ['maintenance', 'Mantenimiento'],
        ['decom', 'Decom'],
        ['planned', 'Planificado'],
      ]),
    },
    { key: 'vendor', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
    { key: 'firmware_version', label: 'Firmware', type: 'text' },
  ],

  firewalls: [
    { key: 'status', label: 'Estado', type: 'select', options: STATUS_HA },
    { key: 'vendor', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
    { key: 'firmware_version', label: 'Firmware', type: 'text' },
    {
      key: 'ha_role',
      label: 'Rol en HA',
      type: 'select',
      options: opts([
        ['', 'Sin HA'],
        ['primary', 'Primario'],
        ['secondary', 'Secundario'],
        ['standalone', 'Standalone'],
      ]),
    },
  ],

  routers: [
    { key: 'status', label: 'Estado', type: 'select', options: STATUS_HA },
    { key: 'vendor', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
    { key: 'firmware_version', label: 'Firmware', type: 'text' },
    { key: 'ios_version', label: 'Versión de IOS', type: 'text' },
  ],

  ups: [
    {
      key: 'status',
      label: 'Estado',
      type: 'select',
      options: opts([
        ['active', 'Activo'],
        ['standby', 'Standby'],
        ['maintenance', 'Mantenimiento'],
        ['decom', 'Decom'],
      ]),
    },
    { key: 'vendor', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
    { key: 'battery_type', label: 'Tipo de batería', type: 'text' },
    { key: 'battery_replace_date', label: 'Sustitución de baterías', type: 'date' },
  ],

  chassis: [
    { key: 'status', label: 'Estado', type: 'select', options: STATUS_ASSET_LONG },
    { key: 'manufacturer', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
  ],

  virt_vms: [
    { key: 'environment', label: 'Entorno', type: 'select', options: ENVIRONMENT },
    {
      key: 'status',
      label: 'Estado',
      type: 'select',
      options: opts([
        ['running', 'Encendida'],
        ['stopped', 'Apagada'],
        ['suspended', 'Suspendida'],
        ['deleted', 'Eliminada'],
      ]),
    },
    { key: 'os_name', label: 'Sistema operativo', type: 'text' },
    { key: 'os_version', label: 'Versión del SO', type: 'text' },
    { key: 'notes', label: 'Notas', type: 'text' },
  ],

  cameras: [
    { key: 'status', label: 'Estado', type: 'select', options: STATUS_ASSET_LONG },
    { key: 'manufacturer', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
  ],

  recorders: [
    { key: 'status', label: 'Estado', type: 'select', options: STATUS_ASSET_LONG },
    { key: 'manufacturer', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
    { key: 'firmware_version', label: 'Firmware', type: 'text' },
  ],

  licenses: [
    {
      key: 'status',
      label: 'Estado',
      type: 'select',
      options: opts([
        ['active', 'Activa'],
        ['in_renewal', 'En renovación'],
        ['expired', 'Caducada'],
        ['cancelled', 'Cancelada'],
      ]),
    },
    { key: 'vendor', label: 'Proveedor', type: 'text' },
    { key: 'expiry_date', label: 'Caducidad', type: 'date' },
  ],

  websites: [
    {
      key: 'status',
      label: 'Estado',
      type: 'select',
      options: opts([
        ['online', 'Online'],
        ['offline', 'Offline'],
        ['maintenance', 'Mantenimiento'],
      ]),
    },
    { key: 'tls_source', label: 'Origen del TLS', type: 'text' },
  ],

  backup_policies: [
    {
      key: 'status',
      label: 'Estado',
      type: 'select',
      options: opts([
        ['active', 'Activa'],
        ['disabled', 'Deshabilitada'],
        ['error', 'Con error'],
      ]),
    },
    { key: 'encryption', label: 'Cifrado', type: 'text' },
  ],

  database_instances: [
    {
      key: 'status',
      label: 'Estado',
      type: 'select',
      options: opts([
        ['online', 'Online'],
        ['offline', 'Offline'],
        ['maintenance', 'Mantenimiento'],
      ]),
    },
    { key: 'dbms', label: 'Motor', type: 'text' },
    { key: 'version', label: 'Versión', type: 'text' },
  ],

  racks: [
    {
      key: 'status',
      label: 'Estado',
      type: 'select',
      options: opts([
        ['draft', 'Borrador'],
        ['live', 'En producción'],
        ['retired', 'Retirado'],
      ]),
    },
    { key: 'manufacturer', label: 'Fabricante', type: 'text' },
    { key: 'model', label: 'Modelo', type: 'text' },
  ],

  locations: [
    { key: 'is_active', label: 'Activa', type: 'bool' },
  ],

  dns_domains: [
    {
      key: 'status',
      label: 'Estado',
      type: 'select',
      options: opts([
        ['active', 'Activo'],
        ['expired', 'Caducado'],
        ['pending', 'Pendiente'],
        ['unknown', 'Desconocido'],
        ['transferred', 'Transferido'],
      ]),
    },
    { key: 'registrar', label: 'Registrador', type: 'text' },
    { key: 'expires_at', label: 'Caducidad', type: 'date' },
    { key: 'auto_renew', label: 'Renovación automática', type: 'bool' },
  ],
};

/** ¿Esta entidad tiene edición masiva de campos? */
export function hasBulkEdit(entity) {
  return Array.isArray(BULK_FIELD_REGISTRY[entity]) && BULK_FIELD_REGISTRY[entity].length > 0;
}

export function bulkFields(entity) {
  return BULK_FIELD_REGISTRY[entity] || [];
}
